// Weergave van supportgesprekken: wachttijden, doorlooptijden, tijdstippen en de klant van een gesprek, zoals in het
// prototype (proto-support-logic.js).
import type { SupportConversation, SupportDevRef } from "./api-support";
import { firstName } from "./planning";

export const MONTHS = ["januari", "februari", "maart", "april", "mei", "juni", "juli", "augustus", "september", "oktober", "november", "december"];
export const nOf = (x: number, one: string, many: string) => x + " " + (x === 1 ? one : many);
export const sum = (a: number[]) => a.reduce((x, y) => x + y, 0);
/** één decimaal met komma, zonder ",0" */
export const fmtNum = (x: number) => String(Math.round(x * 10) / 10).replace(".", ",");

/** uren sinds een tijdstip */
export const hoursSince = (iso: string, now = Date.now()) => Math.max(0, (now - Date.parse(iso)) / 36e5);

/** "2 d 4 u", "20 u", "35 min" */
export function waitText(h: number): string {
  if (h < 1) return Math.max(1, Math.round(h * 60)) + " min";
  if (h < 24) return Math.floor(h) + " u";
  const d = Math.floor(h / 24), r = Math.floor(h - d * 24);
  return d + " d" + (r ? " " + r + " u" : "");
}
export const waitCls = (h: number) => (h >= 48 ? "t-err" : h >= 24 ? "t-warn" : "t-mute");

/** doorlooptijd: "3 u", "1,8 d"; null = niet af te leiden */
export function durText(h: number | null): string {
  if (h == null) return "–";
  if (h < 1) return Math.max(1, Math.round(h * 60)) + " min";
  if (h < 24) return Math.round(h) + " u";
  return fmtNum(h / 24) + " d";
}

const WD: Record<string, string> = { Mon: "ma", Tue: "di", Wed: "wo", Thu: "do", Fri: "vr", Sat: "za", Sun: "zo" };
const FMT = new Intl.DateTimeFormat("en-GB", { timeZone: "Europe/Brussels", weekday: "short", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", hourCycle: "h23" });
/** "wo 07/10 14:32" in Brussel */
export function when(iso: string): string {
  const p = Object.fromEntries(FMT.formatToParts(new Date(iso)).map((x) => [x.type, x.value]));
  return (WD[p.weekday] || p.weekday) + " " + p.day + "/" + p.month + " " + p.hour + ":" + p.minute;
}
export const hhmm = (iso: string) => when(iso).slice(-5);

/** Klant van een gesprek, kort: de klantnaam, of waarom er geen is. */
export function klantText(s: Pick<SupportConversation, "klant" | "domain" | "domainState">): string {
  if (s.klant) return s.klant;
  if (s.domainState === "onbekend") return "onbekend domein";
  if (s.domainState === "genegeerd") return s.domain;
  return "intern";
}

/** Naam van een developer: voornaam als de categorie bij een persoon hoort, anders de categorie zelf. */
export const devLabel = (d: SupportDevRef) => (d.personId ? firstName(d.name) : d.name);

/** Initialen van een klant (vierkant avatar); "?" voor zonder klant. */
export function kInit(name: string): string {
  if (!name) return "?";
  const w = name.replace(/\./g, "").split(/\s+/).filter(Boolean);
  return (w.length > 1 ? w[0][0] + w[1][0] : name.slice(0, 2)).toUpperCase();
}

const norm = (s: string) => s.toLowerCase().normalize("NFD").replace(/[^a-z0-9]/g, "");
/** Voorstel op naam: het domein zonder extensie bevat de klantnaam (devreese-transport.be en de Vreese). */
export function suggestKlant<K extends { id: string; name: string }>(domain: string, klanten: K[]): K | null {
  const label = norm(domain.split(".").slice(0, -1).join(""));
  if (label.length < 3) return null;
  return klanten.find((k) => { const n = norm(k.name); return n.length >= 3 && (label.includes(n) || n.includes(label)); }) || null;
}
